import React, { useState } from 'react'
import { 
  Layout, 
  Row, 
  Col, 
  Card, 
  Button, 
  Typography, 
  Form,
  Input,
  Select,
  Space,
  Breadcrumb,
  Divider,
  List,
     // Tag removed - not used
} from 'antd'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  HomeOutlined, 
  PhoneOutlined,
  MailOutlined,
  MessageOutlined,
  QuestionCircleOutlined,
  CustomerServiceOutlined,
     // ClockCircleOutlined removed - not used
} from '@ant-design/icons'
import { Helmet } from 'react-helmet-async'
import { useQuery } from '@tanstack/react-query'
import { siteSettingsApi } from '../../services/api'

const { Content } = Layout
const { Title, Text, Paragraph } = Typography
const { TextArea } = Input
const { Option } = Select

const SupportPage: React.FC = () => {
  const { i18n, t } = useTranslation()
  const currentLang = i18n.language as 'en' | 'zh'
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  // Fetch site settings for dynamic content
  const { data: siteSettingsData } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: siteSettingsApi.getPublicSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const settings = siteSettingsData?.settings

  // Helper function to get localized text
  const getLocalizedText = (text: { en: string; zh: string } | undefined, fallback: string = '') => {
    if (!text) return fallback
    return text[currentLang] || text.en || fallback
  }

  const handleSubmit = async (values: any) => {
    setLoading(true)
    try {
      // Here you would typically send the form data to your API
      console.log('Support form submitted:', values)
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      form.resetFields()
      // Show success message
    } catch (error) {
      console.error('Failed to submit support form:', error)
    } finally {
      setLoading(false)
    }
  }

  const faqItems = [
    {
      question: "How do I calibrate my blood pressure monitor?",
      answer: "Our blood pressure monitors are factory-calibrated and don't require user calibration. If you suspect accuracy issues, please contact our support team for assistance."
    },
    {
      question: "What should I do if my device isn't working properly?",
      answer: "First, check the user manual for troubleshooting steps. Ensure batteries are fresh and connections are secure. If issues persist, contact our technical support team."
    },
    {
      question: "How often should I replace the batteries?",
      answer: "Battery life varies by usage, but typically lasts 6-12 months with normal use. Replace batteries when the low battery indicator appears."
    },
    {
      question: "Where can I find replacement parts and accessories?",
      answer: "Replacement parts and accessories are available through our authorized dealers or by contacting our customer service team directly."
    }
  ]

  return (
    <>
      <Helmet>
        <title>{t('support.title')} - {getLocalizedText(settings?.siteName, 'MedCareHub')}</title>
        <meta name="description" content={t('support.subtitle')} />
      </Helmet>

      <Content>
        {/* Breadcrumb */}
        <div className="bg-gray-50 py-4">
          <div className="max-w-6xl mx-auto px-4">
            <Breadcrumb
              items={[
                {
                  title: (
                    <Link to="/">
                      <HomeOutlined /> {t('navigation.home')}
                    </Link>
                  ),
                },
                {
                  title: t('navigation.support'),
                },
              ]}
            />
          </div>
        </div>

        {/* Banner Section */}
        <section 
          className="h-[40vh] text-white relative bg-center bg-cover flex items-center justify-center"
          style={{
            backgroundImage: `url('${settings?.pages?.support?.bannerImage || '/support-banner.jpg'}')`,
            backgroundColor: '#1890ff' // Fallback color
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
            <Title level={1} className="text-4xl font-bold text-white mb-4 drop-shadow-lg" style={{ color: '#ffffff' }}>
              {getLocalizedText(settings?.pages?.support?.title) || t('support.title')}
            </Title>
            <Paragraph className="text-xl text-white max-w-3xl mx-auto drop-shadow-lg">
              {getLocalizedText(settings?.pages?.support?.subtitle) || t('support.subtitle')}
            </Paragraph>
          </div>
        </section>

        {/* Header */}
        <section className="py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <Title level={2} className="text-3xl font-bold text-gray-900 mb-4">
              {t('support.contactOptions.cardTitle', 'Contact Support')}
            </Title>
            <Text className="text-lg text-gray-600">
              {getLocalizedText(settings?.pages?.support?.responseTimeNote) || getLocalizedText(settings?.support?.responseTime, 'We respond within 24 hours')}
            </Text>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 py-8">
          <Row gutter={[32, 32]}>
            {/* Contact Options */}
            <Col xs={24} lg={8}>
              <Space direction="vertical" size="large" className="w-full">
                <Card title={t('support.contactOptions.cardTitle', 'Contact Support')} className="h-full">
                  <Space direction="vertical" size="middle" className="w-full">
                    {settings?.contact?.phone && (
                      <div className="flex items-center">
                        <PhoneOutlined className="text-blue-600 mr-3" />
                        <div>
                          <div className="font-medium">{t('support.contactOptions.phoneSupport', 'Phone Support')}</div>
                          <div className="text-gray-600">{settings.contact.phone}</div>
                          <div className="text-sm text-gray-500">{t('support.contactOptions.phoneHours', 'Mon-Fri, 9:00-18:00')}</div>
                        </div>
                      </div>
                    )}

                    {settings?.contact?.email && (
                      <div className="flex items-center">
                        <MailOutlined className="text-blue-600 mr-3" />
                        <div>
                          <div className="font-medium">{t('support.contactOptions.emailSupport', 'Email Support')}</div>
                          <div className="text-gray-600">{settings.contact.email}</div>
                          <div className="text-sm text-gray-500">{t('support.contactOptions.emailResponse', 'Response within 24 hours')}</div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center">
                      <MessageOutlined className="text-blue-600 mr-3" />
                      <div>
                        <div className="font-medium">{t('support.contactOptions.liveChat', 'Live Chat')}</div>
                        <div className="text-gray-600">{t('support.contactOptions.availableNow', 'Available now')}</div>
                        <div className="text-sm text-gray-500">{t('support.contactOptions.instantAssistance', 'Instant assistance')}</div>
                      </div>
                    </div>
                  </Space>
                </Card>

                <Card title={t('footer.quickLinks.title')}>
                  <List
                    size="small"
                    dataSource={[
                      { title: t('support.downloads.manuals'), link: '/downloads' },
                      { title: t('support.sections.warranty'), link: '/warranty' },
                      { title: t('support.quickLinks.productRegistration', 'Product Registration'), link: '/register' },
                      { title: t('navigation.storeLocator'), link: '/store-locator' },
                    ]}
                    renderItem={item => (
                      <List.Item>
                        <Link to={item.link} className="text-blue-600 hover:text-blue-800">
                          {item.title}
                        </Link>
                      </List.Item>
                    )}
                  />
                </Card>
              </Space>
            </Col>

            {/* Contact Form */}
            <Col xs={24} lg={16}>
              <Card title={t('support.sections.contactForm')} className="mb-8">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  size="large"
                >
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="name"
                        label={t('support.contact.form.name')}
                        rules={[{ required: true, message: t('messages.validation.required') }]}
                      >
                        <Input placeholder={t('support.contact.form.name')} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="email"
                        label={t('support.contact.form.email')}
                        rules={[
                          { required: true, message: t('messages.validation.required') },
                          { type: 'email', message: t('messages.validation.email') }
                        ]}
                      >
                        <Input placeholder={t('support.contact.form.email')} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="phone"
                        label={`${t('support.contact.form.phone')} (${t('common.optional')})`}
                      >
                        <Input placeholder={t('support.contact.form.phone')} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="subject"
                        label={t('support.contact.form.subject')}
                        rules={[{ required: true, message: t('messages.validation.required') }]}
                      >
                        <Select placeholder={t('common.select')}>
                          <Option value="technical">{t('support.contact.categories.technical')}</Option>
                          <Option value="product">{t('support.contact.categories.product')}</Option>
                          <Option value="warranty">{t('support.contact.categories.warranty')}</Option>
                          <Option value="general">{t('support.contact.categories.general')}</Option>
                          <Option value="other">{t('support.contact.categories.other')}</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="message"
                    label={t('support.contact.form.message')}
                    rules={[{ required: true, message: t('messages.validation.required') }]}
                  >
                    <TextArea 
                      rows={6} 
                      placeholder={t('support.contact.form.message')}
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      loading={loading}
                      size="large"
                    >
                      {t('support.contact.form.submit')}
                    </Button>
                  </Form.Item>
                </Form>
              </Card>

              {/* FAQ Section */}
              <Card title={<><QuestionCircleOutlined className="mr-2" />{t('support.sections.faqs')}</>}>
                <Space direction="vertical" size="large" className="w-full">
                  {faqItems.map((item, index) => (
                    <div key={index}>
                      <Title level={5} className="text-gray-800 mb-2">
                        {item.question}
                      </Title>
                      <Paragraph className="text-gray-600 mb-0">
                        {item.answer}
                      </Paragraph>
                      {index < faqItems.length - 1 && <Divider />}
                    </div>
                  ))}
                </Space>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Support Hours */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <Title level={2} className="mb-8">
              <CustomerServiceOutlined className="mr-3" />
              {t('support.hours.title', 'Support Hours')}
            </Title>
            <Row gutter={[32, 16]}>
              <Col xs={24} md={8}>
                <Card className="text-center">
                  <PhoneOutlined className="text-2xl text-blue-600 mb-3" />
                  <Title level={4}>{t('support.contactOptions.phoneSupport', 'Phone Support')}</Title>
                  <Text className="text-gray-600">
                    {t('support.hours.weekdays', 'Monday - Friday')}<br />
                    {t('support.hours.phoneSchedule', '9:00 AM - 6:00 PM HKT')}
                  </Text>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card className="text-center">
                  <MailOutlined className="text-2xl text-blue-600 mb-3" />
                  <Title level={4}>{t('support.contactOptions.emailSupport', 'Email Support')}</Title>
                  <Text className="text-gray-600">
                    {t('support.hours.emailAvailability', '24/7 Support')}<br />
                    {t('support.contactOptions.emailResponse', 'Response within 24 hours')}
                  </Text>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card className="text-center">
                  <MessageOutlined className="text-2xl text-blue-600 mb-3" />
                  <Title level={4}>{t('support.contactOptions.liveChat', 'Live Chat')}</Title>
                  <Text className="text-gray-600">
                    {t('support.hours.weekdays', 'Monday - Friday')}<br />
                    {t('support.hours.liveChatSchedule', '9:00 AM - 9:00 PM HKT')}
                  </Text>
                </Card>
              </Col>
            </Row>
          </div>
        </section>
      </Content>
    </>
  )
}

export default SupportPage
